import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Clock,
  Download,
  ExternalLink,
  Loader2,
  Package,
  PieChart,
  Plus,
  Search,
  ShoppingCart,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  farmGet,
  farmPatch,
  STATO_ORDINE_LABELS,
  URGENZA_ORDINE_LABELS,
} from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

const FILTERS = [
  { id: "tutti", label: "Tutti" },
  { id: "strutture", label: "Strutture" },
  { id: "clienti", label: "Clienti diretti" },
  { id: "urgenti", label: "Urgenti" },
  { id: "parziali", label: "Parziali" },
];

const URGENZA_RANK = { critica: 0, urgente: 1, normale: 2 };

const DA_CONFERMARE = new Set(["inviato", "ricevuto"]);
const IN_PREPARAZIONE = new Set(["confermato", "in_preparazione"]);
const IN_CONSEGNA = new Set(["pronto", "spedito"]);

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function formatShortDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
}

function formatEuro(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `€ ${Number(n).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function ordineTotale(o) {
  let tot = 0;
  let has = false;
  for (const r of o.righe || []) {
    if (r.prezzo != null) {
      has = true;
      const q = Number(r.quantita_confermata ?? r.quantita_richiesta ?? r.quantita ?? 1);
      tot += Number(r.prezzo) * q;
    }
  }
  return has ? tot : null;
}

function ordineOspite(o) {
  const names = [
    ...new Set(
      (o.righe || [])
        .map((r) => r.ospite_nome)
        .filter(Boolean)
    ),
  ];
  if (!names.length) return "—";
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

function isClienteDiretto(o) {
  const orig = (o.origine || "").toLowerCase();
  return orig.includes("cliente") || orig === "cliente_diretto" || orig === "diretto";
}

function shortId(id) {
  if (!id) return "—";
  const s = String(id);
  return s.length > 10 ? `ORD-${s.slice(0, 8).toUpperCase()}` : s;
}

function KpiTile({ icon: Icon, label, value, tone }) {
  const tones = {
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
    yellow: "bg-yellow-50 text-yellow-800",
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-slate-500">{label}</p>
          <p className="mt-0.5 text-xl font-semibold tracking-tight text-slate-900">{value}</p>
        </div>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tones[tone] || tones.sky}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

function ChannelDonut({ strutture, clienti }) {
  const total = strutture + clienti;
  const pctStr = total ? Math.round((strutture / total) * 100) : 0;
  const pctCli = total ? 100 - pctStr : 0;
  const gradient = total
    ? `conic-gradient(#3b82f6 0 ${pctStr}%, #0F5C4C ${pctStr}% 100%)`
    : "conic-gradient(#e2e8f0 0 100%)";
  return (
    <div className="flex items-center gap-4">
      <div className="relative h-24 w-24 shrink-0 rounded-full" style={{ background: gradient }}>
        <div className="absolute inset-3 flex flex-col items-center justify-center rounded-full bg-white">
          <span className="text-lg font-semibold text-slate-900">{total}</span>
          <span className="text-[10px] text-slate-400">Totali</span>
        </div>
      </div>
      <ul className="space-y-2 text-xs">
        <li className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
          <span className="text-slate-600">Strutture</span>
          <span className="ml-auto font-semibold tabular-nums text-slate-900">{pctStr}%</span>
        </li>
        <li className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-[#0F5C4C]" />
          <span className="text-slate-600">Clienti diretti</span>
          <span className="ml-auto font-semibold tabular-nums text-slate-900">{pctCli}%</span>
        </li>
      </ul>
    </div>
  );
}

function OrdineDetailPanel({ ordine, busy, onAction, onClose }) {
  const navigate = useNavigate();
  const [deliveryDate, setDeliveryDate] = useState(ordine?.data_consegna || "");

  useEffect(() => {
    setDeliveryDate(ordine?.data_consegna || "");
  }, [ordine?.id, ordine?.data_consegna]);

  if (!ordine) {
    return (
      <aside className="hidden w-[340px] shrink-0 xl:block">
        <div className="sticky top-0 rounded-2xl border border-dashed border-slate-200 bg-white/60 p-6 text-center">
          <ShoppingCart className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Ordine selezionato</p>
          <p className="mt-1 text-xs text-slate-400">Seleziona una riga dall&apos;elenco per il dettaglio.</p>
        </div>
      </aside>
    );
  }

  const st = STATO_ORDINE_LABELS[ordine.stato] || {
    label: ordine.stato,
    className: "bg-slate-50 text-slate-600 border-slate-200",
  };
  const cliente = isClienteDiretto(ordine);
  const ospite = ordineOspite(ordine);

  const run = (azione) => onAction(ordine.id, { azione, data_consegna: deliveryDate || null });

  return (
    <aside className="w-full shrink-0 xl:w-[340px]">
      <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-mono text-xs font-semibold text-slate-500">{shortId(ordine.id)}</p>
              <h3 className="mt-0.5 truncate text-sm font-semibold text-slate-900">
                {ordine.struttura_nome || (cliente ? "Cliente diretto" : "Ordine")}
              </h3>
              <p className="mt-0.5 truncate text-xs text-slate-500">{ospite}</p>
            </div>
            <button
              type="button"
              className="text-xs text-slate-400 hover:text-slate-600 xl:hidden"
              onClick={onClose}
            >
              Chiudi
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge
              variant="outline"
              className={`text-[10px] ${
                cliente
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-sky-200 bg-sky-50 text-sky-700"
              }`}
            >
              {cliente ? "Cliente diretto" : "Struttura"}
            </Badge>
            <Badge variant="outline" className={`text-[10px] ${st.className}`}>
              {st.label}
            </Badge>
            {ordine.urgenza && ordine.urgenza !== "normale" ? (
              <Badge variant="outline" className="border-amber-200 text-[10px] text-amber-700">
                {URGENZA_ORDINE_LABELS[ordine.urgenza] || ordine.urgenza}
              </Badge>
            ) : null}
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Articoli ordine
            </p>
            <ul className="space-y-1.5">
              {(ordine.righe || []).slice(0, 8).map((r, idx) => (
                <li
                  key={idx}
                  className="flex items-start justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-xs"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">{r.prodotto_nome || r.prodotto_codice || "—"}</p>
                    <p className="text-[10px] text-slate-400">
                      qty {r.quantita_richiesta ?? r.quantita ?? "—"}
                      {r.ospite_nome ? ` · ${r.ospite_nome}` : ""}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 text-[10px] font-medium ${
                      r.da_identificare || r.stato === "da_identificare"
                        ? "text-amber-700"
                        : "text-emerald-700"
                    }`}
                  >
                    {r.da_identificare || r.stato === "da_identificare" ? "Da verificare" : "Disponibile"}
                  </span>
                </li>
              ))}
              {(ordine.righe || []).length === 0 ? (
                <li className="text-xs text-slate-400">Nessuna riga</li>
              ) : null}
            </ul>
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" htmlFor={`data-consegna-${ordine.id}`}>
              Data prevista di consegna
            </label>
            <div className="flex gap-2">
              <Input
                id={`data-consegna-${ordine.id}`}
                type="date"
                value={deliveryDate}
                onChange={(event) => setDeliveryDate(event.target.value)}
                className="h-8 text-xs"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 shrink-0 text-[11px]"
                disabled={busy || deliveryDate === (ordine.data_consegna || "")}
                onClick={() => onAction(ordine.id, { data_consegna: deliveryDate || null })}
              >
                Salva
              </Button>
            </div>
            {ordine.consegnato_at ? (
              <p className="mt-1.5 text-[10px] text-slate-400">Consegnato il {formatWhen(ordine.consegnato_at)}</p>
            ) : null}
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Azioni rapide
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {ordine.stato === "inviato" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 rounded-lg text-[11px]"
                  disabled={busy}
                  onClick={() => run("marca_ricevuto")}
                >
                  Segna ricevuto
                </Button>
              ) : null}
              {["inviato", "ricevuto", "parziale"].includes(ordine.stato) ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 rounded-lg bg-[#0F5C4C] text-[11px] hover:bg-[#0c4a3e]"
                    disabled={busy}
                    onClick={() => run("conferma_totale")}
                  >
                    Conferma ordine
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-lg text-[11px]"
                    disabled={busy}
                    onClick={() => run("conferma_parziale")}
                  >
                    Conferma parziale
                  </Button>
                </>
              ) : null}
              {["confermato", "parziale"].includes(ordine.stato) ? (
                <Button
                  type="button"
                  size="sm"
                  className="h-8 rounded-lg bg-[#0F5C4C] text-[11px] hover:bg-[#0c4a3e]"
                  disabled={busy}
                  onClick={() => run("in_preparazione")}
                >
                  In preparazione
                </Button>
              ) : null}
              {ordine.stato === "in_preparazione" ? (
                <Button
                  type="button"
                  size="sm"
                  className="h-8 rounded-lg bg-[#0F5C4C] text-[11px] hover:bg-[#0c4a3e]"
                  disabled={busy}
                  onClick={() => run("pronto")}
                >
                  Segna pronto
                </Button>
              ) : null}
              {ordine.stato === "pronto" ? (
                <Button
                  type="button"
                  size="sm"
                  className="h-8 rounded-lg bg-[#0F5C4C] text-[11px] hover:bg-[#0c4a3e]"
                  disabled={busy}
                  onClick={() => run("consegnato")}
                >
                  Consegnato
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 rounded-lg text-[11px]"
                onClick={() => navigate("/farmacia/consegne")}
              >
                Vai a consegne
              </Button>
            </div>
            {busy ? <Loader2 className="mt-2 h-4 w-4 animate-spin text-emerald-600" /> : null}
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Cronologia
            </p>
            <ol className="relative space-y-3 border-l border-slate-200 pl-3 text-xs text-slate-600">
              <li>
                <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-[#0F5C4C]" />
                Creato
                <span className="mt-0.5 block text-[10px] text-slate-400">
                  {formatWhen(ordine.created_at)}
                </span>
              </li>
              {ordine.updated_at && ordine.updated_at !== ordine.created_at ? (
                <li>
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-sky-400" />
                  Aggiornato · {st.label}
                  <span className="mt-0.5 block text-[10px] text-slate-400">
                    {formatWhen(ordine.updated_at)}
                  </span>
                </li>
              ) : null}
            </ol>
          </div>
        </div>

        <div className="mt-auto border-t border-slate-100 p-3">
          <p className="mb-2 text-center text-xs text-slate-500">
            Totale {formatEuro(ordineTotale(ordine))} · {(ordine.righe || []).length} articoli
          </p>
          <Button asChild className="h-10 w-full rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
            <Link to="/farmacia/consegne">
              Gestisci consegna
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </aside>
  );
}

export default function FarmaciaOrdini() {
  const { token } = useFarmaciaAuth();
  const navigate = useNavigate();
  const [ordini, setOrdini] = useState([]);
  const [strutture, setStrutture] = useState([]);
  const [consegne, setConsegne] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("tutti");
  const [orgFilter, setOrgFilter] = useState("all");
  const [statoFilter, setStatoFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const [ordRes, strRes, consRes] = await Promise.all([
        farmGet("/farmacia/ordini", token).catch(() => ({ items: [] })),
        farmGet("/farmacia/strutture", token, { lite: true }).catch(() => ({ items: [] })),
        farmGet("/farmacia/consegne", token, { data: today }).catch(() => ({ items: [] })),
      ]);
      const items = (ordRes.items || []).slice().sort((a, b) => {
        const ua = URGENZA_RANK[a.urgenza] ?? 9;
        const ub = URGENZA_RANK[b.urgenza] ?? 9;
        if (ua !== ub) return ua - ub;
        return String(b.created_at || "").localeCompare(String(a.created_at || ""));
      });
      setOrdini(items);
      setStrutture(strRes.items || []);
      setConsegne(consRes.items || consRes.consegne || []);
      setSelectedId((prev) => {
        if (prev && items.some((o) => o.id === prev)) return prev;
        return items[0]?.id || null;
      });
    } catch {
      setOrdini([]);
      toast.error("Impossibile caricare gli ordini");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const onAction = async (ordineId, body) => {
    setBusyId(ordineId);
    try {
      await farmPatch(`/farmacia/ordini/${encodeURIComponent(ordineId)}`, token, body);
      toast.success("Ordine aggiornato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento ordine fallito");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    let list = ordini;
    if (orgFilter !== "all") {
      list = list.filter(
        (o) =>
          o.organization_id === orgFilter ||
          (o.organization_ids || []).includes(orgFilter)
      );
    }
    if (statoFilter !== "all") {
      list = list.filter((o) => o.stato === statoFilter);
    }
    if (filter === "strutture") list = list.filter((o) => !isClienteDiretto(o));
    if (filter === "clienti") list = list.filter((o) => isClienteDiretto(o));
    if (filter === "urgenti") {
      list = list.filter((o) => o.urgenza && o.urgenza !== "normale");
    }
    if (filter === "parziali") list = list.filter((o) => o.stato === "parziale");
    const term = q.trim().toLowerCase();
    if (term) {
      list = list.filter((o) => {
        const blob = [
          o.id,
          o.struttura_nome,
          o.stato,
          o.note,
          ...(o.righe || []).flatMap((r) => [r.prodotto_nome, r.ospite_nome, r.codice_fiscale]),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(term);
      });
    }
    return list;
  }, [ordini, filter, orgFilter, statoFilter, q]);

  const selected = ordini.find((o) => o.id === selectedId) || null;

  const kpi = useMemo(() => {
    const attivi = ordini.filter((o) => !["consegnato", "annullato", "rifiutato"].includes(o.stato));
    return {
      attivi: attivi.length,
      daConfermare: ordini.filter((o) => DA_CONFERMARE.has(o.stato)).length,
      inPrep: ordini.filter((o) => IN_PREPARAZIONE.has(o.stato)).length,
      parziali: ordini.filter((o) => o.stato === "parziale").length,
      inConsegna: ordini.filter((o) => IN_CONSEGNA.has(o.stato)).length,
      completati: ordini.filter((o) => o.stato === "consegnato").length,
    };
  }, [ordini]);

  const channel = useMemo(() => {
    const clienti = ordini.filter(isClienteDiretto).length;
    const strutture = ordini.length - clienti;
    return { strutture, clienti };
  }, [ordini]);

  const alerts = useMemo(() => {
    const daId = ordini.reduce(
      (n, o) =>
        n +
        (o.righe || []).filter((r) => r.da_identificare || r.stato === "da_identificare").length,
      0
    );
    return [
      { label: "Prodotti non identificati", n: daId, tone: "amber" },
      { label: "Ordini in attesa conferma", n: kpi.daConfermare, tone: "sky" },
      {
        label: "Consegne da ripianificare",
        n: consegne.filter((c) => c.stato === "riprogrammata" || c.stato === "problema").length,
        tone: "rose",
      },
    ].filter((a) => a.n > 0);
  }, [ordini, kpi, consegne]);

  const exportCsv = () => {
    const header = ["ID", "Data", "Canale", "Struttura", "Ospite", "Articoli", "Totale", "Stato", "Urgenza"];
    const rows = filtered.map((o) => [
      o.id,
      o.created_at || "",
      isClienteDiretto(o) ? "Cliente diretto" : "Struttura",
      o.struttura_nome || "",
      ordineOspite(o),
      (o.righe || []).length,
      ordineTotale(o) ?? "",
      o.stato || "",
      o.urgenza || "",
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ordini-farmacia-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Esportazione avviata");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="min-w-0 flex-1 space-y-5">
        <FarmaciaPageChrome>
          <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={exportCsv}>
            <Download className="mr-1.5 h-4 w-4" />
            Esporta
          </Button>
          <Button
            type="button"
            size="sm"
            className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
            onClick={() =>
              toast.message("Gli ordini arrivano dalle strutture (extra blister)", {
                description: "Puoi crearli anche da API; in UI la coda operativa è qui.",
              })
            }
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Nuovo ordine
          </Button>
        </FarmaciaPageChrome>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
          <KpiTile icon={ShoppingCart} label="Ordini attivi" value={kpi.attivi} tone="sky" />
          <KpiTile icon={Clock} label="Da confermare" value={kpi.daConfermare} tone="amber" />
          <KpiTile icon={ClipboardList} label="In preparazione" value={kpi.inPrep} tone="violet" />
          <KpiTile icon={PieChart} label="Parziali" value={kpi.parziali} tone="yellow" />
          <KpiTile icon={Truck} label="In consegna / pronti" value={kpi.inConsegna} tone="blue" />
          <KpiTile icon={CheckCircle2} label="Completati" value={kpi.completati} tone="emerald" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex max-w-full flex-wrap gap-1.5 rounded-xl bg-slate-100/80 p-1">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition sm:text-xs ${
                  filter === f.id
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca ordine, ospite, prodotto…"
                className="h-9 w-52 rounded-xl pl-8 text-xs sm:w-64"
              />
            </div>
            <select
              className="h-9 rounded-xl border border-slate-200 bg-white px-2.5 text-xs text-slate-700"
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
            >
              <option value="all">Tutte le strutture</option>
              {strutture.map((s) => (
                <option key={s.organization_id} value={s.organization_id}>
                  {s.denominazione || s.nome}
                </option>
              ))}
            </select>
            <select
              className="h-9 rounded-xl border border-slate-200 bg-white px-2.5 text-xs text-slate-700"
              value={statoFilter}
              onChange={(e) => setStatoFilter(e.target.value)}
            >
              <option value="all">Tutti gli stati</option>
              {Object.keys(STATO_ORDINE_LABELS).map((k) => (
                <option key={k} value={k}>
                  {STATO_ORDINE_LABELS[k].label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          {filtered.length === 0 ? (
            <div className="px-4 py-16 text-center">
              <Package className="mx-auto mb-3 h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">Nessun ordine</p>
              <p className="mt-1 text-xs text-slate-400">
                Gli ordini extra blister delle strutture compaiono qui per conferma e preparazione.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left text-xs">
                <thead className="border-b border-slate-100 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 font-semibold">Ordine</th>
                    <th className="px-3 py-2.5 font-semibold">Data</th>
                    <th className="px-3 py-2.5 font-semibold">Consegna</th>
                    <th className="px-3 py-2.5 font-semibold">Canale</th>
                    <th className="px-3 py-2.5 font-semibold">Struttura / Cliente</th>
                    <th className="px-3 py-2.5 font-semibold">Ospite</th>
                    <th className="px-3 py-2.5 font-semibold">Art.</th>
                    <th className="px-3 py-2.5 font-semibold">Totale</th>
                    <th className="px-3 py-2.5 font-semibold">Stato</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((o) => {
                    const active = selectedId === o.id;
                    const st = STATO_ORDINE_LABELS[o.stato] || {
                      label: o.stato,
                      className: "bg-slate-50 text-slate-600 border-slate-200",
                    };
                    const cliente = isClienteDiretto(o);
                    return (
                      <tr
                        key={o.id}
                        className={`cursor-pointer ${active ? "bg-emerald-50/50" : "hover:bg-slate-50/80"}`}
                        onClick={() => setSelectedId(o.id)}
                      >
                        <td className="px-4 py-3 font-mono text-[11px] font-semibold text-slate-700">
                          {shortId(o.id)}
                        </td>
                        <td className="px-3 py-3 text-slate-600">{formatShortDate(o.created_at)}</td>
                        <td className="px-3 py-3 text-slate-600">{o.data_consegna || "—"}</td>
                        <td className="px-3 py-3">
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${
                              cliente
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-sky-200 bg-sky-50 text-sky-700"
                            }`}
                          >
                            {cliente ? "Cliente diretto" : "Struttura"}
                          </Badge>
                        </td>
                        <td className="px-3 py-3 font-medium text-slate-800">
                          {o.struttura_nome || "—"}
                        </td>
                        <td className="px-3 py-3 text-slate-600">{ordineOspite(o)}</td>
                        <td className="px-3 py-3 tabular-nums text-slate-700">
                          {(o.righe || []).length}
                        </td>
                        <td className="px-3 py-3 tabular-nums text-slate-700">
                          {formatEuro(ordineTotale(o))}
                        </td>
                        <td className="px-3 py-3">
                          <Badge variant="outline" className={`text-[10px] ${st.className}`}>
                            {st.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="grid gap-3 lg:grid-cols-3">
          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <PieChart className="h-4 w-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-800">Ordini per canale</h3>
            </div>
            <ChannelDonut strutture={channel.strutture} clienti={channel.clienti} />
            <button
              type="button"
              className="mt-3 text-xs font-medium text-emerald-700 hover:underline"
              onClick={() => setFilter(channel.clienti ? "tutti" : "strutture")}
            >
              Vedi dettagli →
            </button>
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Alert ordini</h3>
            {alerts.length === 0 ? (
              <p className="text-xs text-slate-400">Nessun alert attivo</p>
            ) : (
              <ul className="space-y-2">
                {alerts.map((a) => (
                  <li
                    key={a.label}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-xs"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                      <span className="truncate font-medium text-slate-800">
                        {a.n} {a.label.toLowerCase()}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="mt-3 text-xs font-medium text-emerald-700 hover:underline"
              onClick={() => setFilter("urgenti")}
            >
              Vai agli alert →
            </button>
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Prossime consegne</h3>
            {consegne.length === 0 ? (
              <p className="text-xs text-slate-400">Nessuna consegna programmata oggi</p>
            ) : (
              <ul className="space-y-2">
                {consegne.slice(0, 4).map((c) => (
                  <li
                    key={c.id || `${c.organization_id}-${c.data}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-xs"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">
                        {c.struttura_nome || c.organization_id || "Struttura"}
                      </p>
                      <p className="text-slate-400">
                        {c.data}
                        {c.fascia_oraria ? ` · ${c.fascia_oraria}` : ""}
                      </p>
                    </div>
                    <Truck className="h-3.5 w-3.5 shrink-0 text-slate-300" />
                  </li>
                ))}
              </ul>
            )}
            <Link
              to="/farmacia/consegne"
              className="mt-3 inline-block text-xs font-medium text-emerald-700 hover:underline"
            >
              Vedi tutte le consegne →
            </Link>
          </section>
        </div>

        {selected ? (
          <div className="xl:hidden">
            <OrdineDetailPanel
              ordine={selected}
              busy={busyId === selected.id}
              onAction={onAction}
              onClose={() => setSelectedId(null)}
            />
          </div>
        ) : null}
      </div>

      <div className="hidden xl:block">
        <OrdineDetailPanel
          ordine={selected}
          busy={selected ? busyId === selected.id : false}
          onAction={onAction}
          onClose={() => setSelectedId(null)}
        />
      </div>
    </div>
  );
}
